import { useCallback } from "react";
import { SpreadsheetValue } from "@common/helpers/spreadsheet";
import {
    SpreadsheetAccessParams,
    useSpreadsheetQuery,
    useSpreadsheetUpdateMutation,
} from "@api/spreadsheet.api";

interface Change {
    row: number;
    column: string;
    value: SpreadsheetValue;
}

interface ChildrenProps {
    data: Array<Record<string, SpreadsheetValue>>;
    afterChange: (newData: Array<Record<string, SpreadsheetValue>>) => void;
}

interface Props {
    spreadsheetId: string;
    accessParams: SpreadsheetAccessParams;
    afterUpdate?: () => void | Promise<void>;
    children: (props: ChildrenProps) => React.ReactNode;
}

export const PersistentSpreadsheet: React.FC<Props> = ({
    spreadsheetId,
    accessParams,
    afterUpdate,
    children,
}) => {
    const { data } = useSpreadsheetQuery(spreadsheetId, accessParams, {
        refetchOnWindowFocus: false,
    });
    const updateMutation = useSpreadsheetUpdateMutation(
        spreadsheetId,
        accessParams,
        afterUpdate,
    );

    const afterChange = useCallback(
        (newData: Array<Record<string, SpreadsheetValue>>) => {
            const updates = newData.reduce((acc: Change[], row, rowIdx) => {
                Object.entries(row).forEach(([colId, value]) => {
                    const previousValue = data?.[rowIdx]?.[colId];
                    const valueForUpdate = getValueForUpdate(
                        previousValue,
                        value,
                    );
                    if (valueForUpdate !== undefined) {
                        acc.push({
                            row: rowIdx,
                            column: colId,
                            value: valueForUpdate,
                        });
                    }
                });

                return acc;
            }, []);

            if (updates.length) {
                updateMutation.mutate({
                    updates,
                });
            }
        },
        [updateMutation, data],
    );

    if (!data) {
        return null;
    }

    return children({ data, afterChange });
};

function getValueForUpdate(
    previousValue: SpreadsheetValue | undefined,
    newValue: SpreadsheetValue,
) {
    if (
        previousValue === newValue ||
        ((previousValue === undefined || previousValue === null) &&
            (newValue === null || newValue === undefined))
    ) {
        return undefined;
    }

    return newValue ?? null;
}
